import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import styles from '../styles/dashboard.module.css';
import downLoadIcon from '../../../assets/images/downLoadIcon.webp';
import infoIcon from '../../../assets/images/infoIcon.png';
import FeaturePoup from './FeaturePoup';
import { useLocation } from "react-router-dom";
import spinner from '../../../assets/images/spinner_orange.gif';

export default function DashboardPanel() {
    const { currentUser, logout } = useAuth();
    const navigate = useNavigate();
    const [statusData, setStatusData] = useState();
    const [dashboardData, setDashboardData] = useState();
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [togglePopup, setToggleOpup] = useState(false);
    const [toggleCancelAccountWindow, setToggleCancelAccountWindow] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [apiError, setApiError] = useState('');
    const location = useLocation();

    // Configuration constants
    const IDLE_TIMEOUT_HOUR = 1000 * 60 * 60; // 1 hour
    const API_TIMEOUT = 10000; // 10 seconds

    useEffect(() => {
        let isMounted = true; // Flag to prevent state updates if component unmounts

        async function checkPaymentStatus() {
            try {
                const userId = localStorage.getItem('userId');
                if (!userId) {
                    throw new Error('User ID not found in localStorage');
                }

                // Create AbortController for request timeout
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

                const response = await fetch(
                    `${process.env.REACT_APP_PUBLIC_PAYMENT_URL}payment/status?userId=${userId}`,
                    { 
                        signal: controller.signal,
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    }
                );

                clearTimeout(timeoutId);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                
                // Validate response data
                if (data && typeof data === 'object') {
                    if (isMounted) {
                        setStatusData(data);
                    }
                } else {
                    throw new Error('Invalid response data format');
                }
            } catch (error) {
                console.error('Error fetching payment status:', error);
                if (isMounted) {
                    if (error.name === 'AbortError') {
                        setApiError('Request timeout. Please try again.');
                    } else {
                        setApiError(`Failed to fetch payment status: ${error.message}`);
                    }
                }
            }
        }

        async function getDashboard() {
            try {
                const userId = localStorage.getItem('userId');
                if (!userId) {
                    throw new Error('User ID not found in localStorage');
                }

                // Create AbortController for request timeout
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

                const response = await fetch(
                    `${process.env.REACT_APP_PUBLIC_PAYMENT_URL}payment/dashboard?userId=${userId}`,
                    { 
                        signal: controller.signal,
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    }
                );

                clearTimeout(timeoutId);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                
                // Validate response data
                if (data && typeof data === 'object') {
                    if (isMounted) {
                        setDashboardData(data);
                    }
                } else {
                    throw new Error('Invalid response data format');
                }
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                if (isMounted) {
                    if (error.name === 'AbortError') {
                        setApiError('Request timeout. Please try again.');
                    } else {
                        setApiError(`Failed to fetch dashboard data: ${error.message}`);
                    }
                }
            }
        }

        async function initializeDashboard() {
            if (isMounted) {
                setIsLoading(true);
                setApiError('');
            }

            try {
                await Promise.all([checkPaymentStatus(), getDashboard()]);
            } catch (error) {
                console.error('Error initializing dashboard:', error);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        initializeDashboard();

        // Cleanup function
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const currentPage = location.pathname;
        let timer = null;
        let eventListeners = [];

        function inactivityTimeout() {
            if (currentPage === '/dashboard') {
                const resetTimer = () => {
                    if (timer) {
                        clearTimeout(timer);
                    }
                    timer = setTimeout(async () => {
                        try {
                            console.log('User inactive for 1 hour, logging out...');
                            await logout();
                            navigate('/');
                        } catch (error) {
                            console.error('Failed to log out due to inactivity:', error);
                            // Still navigate away even if logout fails
                            navigate('/');
                        }
                    }, IDLE_TIMEOUT_HOUR);
                };

                // Set up event listeners
                const events = ['mousemove', 'mousedown', 'scroll', 'keypress', 'touchstart'];
                events.forEach(event => {
                    const handler = resetTimer;
                    document.addEventListener(event, handler, { passive: true });
                    eventListeners.push({ event, handler });
                });

                // Start the initial timer
                resetTimer();
            }
        }

        try {
            inactivityTimeout();
        } catch (error) {
            console.error('Error setting up inactivity timeout:', error);
        }

        // Cleanup function
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
            // Remove all event listeners
            eventListeners.forEach(({ event, handler }) => {
                document.removeEventListener(event, handler);
            });
        };
    }, [location.pathname, logout, navigate, IDLE_TIMEOUT_HOUR]);



    async function handleLogout() {
        try {
            setIsLoading(true);
            await logout();
            navigate('/');
        } catch (error) {
            console.error('Failed to log out:', error);
            setApiError('Failed to log out. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }

    async function handleCancelAccount() {
        try {
            const userId = localStorage.getItem('userId');
            if (!userId) {
                setError('User ID not found. Please log in again.');
                return;
            }

            setIsLoading(true);
            setError('');
            setMessage('');

            // Create AbortController for request timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

            const response = await fetch(
                `${process.env.REACT_APP_PUBLIC_PAYMENT_URL}cancel/confirm`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ userId: userId }),
                    signal: controller.signal
                }
            );

            clearTimeout(timeoutId);

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                if (data?.error) {
                    setError(data.error);
                } else {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            } else {
                const data = await response.json().catch(() => ({}));
                setMessage(data?.message || 'Your account has been cancelled.');
            }
        } catch (error) {
            console.error('Failed to cancel account:', error);
            if (error.name === 'AbortError') {
                setError('Request timeout. Please try again.');
            } else {
                setError(`Failed to cancel account: ${error.message}`);
            }
        } finally {
            setIsLoading(false);
        }
    }

    const toggleInfoPopup = () => {
        setToggleOpup(!togglePopup);
    }

    const showCancelPopup = () => {
        setError('');
        setMessage('');
        setToggleCancelAccountWindow(true);
    }

    const closeCancelWindow = () => {
        setToggleCancelAccountWindow(false);
        setError('');
        setMessage('');
        // Use window.location.reload() instead of navigate(0) for better compatibility
        window.location.reload();
    }

    return (
        <div>
            {/* Display API errors */}
            {apiError && (
                <div className={styles.errorMessage} style={{ 
                    backgroundColor: '#ffebee', 
                    color: '#c62828', 
                    padding: '10px', 
                    borderRadius: '4px', 
                    marginBottom: '20px',
                    border: '1px solid #ffcdd2'
                }}>
                    {apiError}
                </div>
            )}

            {toggleCancelAccountWindow &&
                <div className={styles.cancelPoup}>
                    {message === '' && error === '' ? <>
                        <p className={styles.cancelTitle}>Are you sure you want to cancel your account?</p>
                        <div className={styles.cancelButtonsContainer}>
                            <button 
                                className={styles.cancelButton} 
                                onClick={handleCancelAccount}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Processing...' : 'Yes'}
                            </button>
                            <button 
                                className={styles.cancelButton} 
                                onClick={() => setToggleCancelAccountWindow(false)}
                                disabled={isLoading}
                            >
                                No
                            </button>
                        </div>
                    </>
                        :
                        <>
                            <p className={styles.cancelTitle}>{message}</p>
                            <p className={styles.cancelTitle}>{error}</p>
                            {!error && message && <p className={styles.cancelParagraph}>You will shortly receive confirmation by email​</p>}
                            <div className={styles.dissmissButtonContainer}>
                                <button className={styles.cancelButton} onClick={closeCancelWindow}>Close</button>
                            </div>
                        </>
                    }
                </div>
            }
            <h2 className={styles.dashboardHeader}>Your Dashboard

                {isLoading && <img src={spinner} alt="Loading.." width={50} />}
            </h2>
            <div className={styles.accountContainer}>
                {togglePopup &&
                    <FeaturePoup closePopup={toggleInfoPopup} />
                }
                <h3 className={styles.sectionTitle}>Account information</h3>
                <div className={styles.userDataContainer}>
                    <div>
                        <p>Email address:</p>
                        <p className={styles.userData}>{currentUser.email}</p>
                    </div>
                    <div className={styles.subscriptionContainer}>
                        <p>Subscription:</p>
                        <div className={styles.subscriptionData}>
                            {!isLoading &&
                                <>
                                    <p className={styles.userData}>{statusData && statusData.isPaid ? 'Device Tracker Plus' : 'Unsubscribed'} </p>
                                    <img src={infoIcon} alt="info_icon" width={20} className={styles.infoIcon} onClick={toggleInfoPopup} />
                                    <p className={styles.stausCanceled}>{statusData && statusData.cancelledDate ? 'Canceled' : ''}</p>
                                </>
                            }
                            
                        </div>
                    </div>

                </div>
            </div>
            <br />
            <div className={styles.accountContainer}>
                <h3 className={styles.sectionTitle}>Billing information - last 12 months</h3>
                <div className={styles.invoiceGrid}>
                    {dashboardData?.invoices && Array.isArray(dashboardData.invoices) ? (
                        dashboardData.invoices.length > 0 ? (
                            dashboardData.invoices.map((item, index) => (
                                <div className={styles.invoiceData} key={index}>
                                    <a 
                                        href={item?.invoicePdf || '#'} 
                                        download={item?.invoicePdf ? undefined : false}
                                        onClick={!item?.invoicePdf ? (e) => e.preventDefault() : undefined}
                                    >
                                        <img 
                                            src={downLoadIcon} 
                                            alt="downLoad_icon" 
                                            width={20} 
                                            style={{ 
                                                marginRight: '10px',
                                                opacity: item?.invoicePdf ? 1 : 0.5 
                                            }} 
                                        />
                                    </a>
                                    <p className={styles.invoiceTitle}>
                                        {item?.name || `Invoice ${index + 1}`}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#666' }}>
                                No invoices available
                            </p>
                        )
                    ) : (
                        <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#666' }}>
                            {isLoading ? 'Loading invoices...' : 'Unable to load invoices'}
                        </p>
                    )}
                </div>
            </div>
            <div className={styles.dashboardButtonsContainer}>
                <div className={styles.buttonContainer} style={{ marginTop: '20px' }}>
                </div>
                <div className={styles.buttonContainer}>
                    <a href={'/help'} className={styles.dashboardButton}>Contact support</a>
                    &nbsp;
                    <button 
                        onClick={showCancelPopup} 
                        className={styles.dashboardButton}
                        disabled={isLoading}
                    >
                        Cancel account
                    </button>
                    &nbsp;
                    <button 
                        onClick={handleLogout} 
                        className={styles.dashboardButton}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Logging out...' : 'Log Out'}
                    </button>
                </div>
            </div>
        </div >
    );
}